import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useApi } from "@/lib/useApi";
import { Badge, Field, PageTitle, btnDanger, btnPrimary, card, inputCls, linkCls } from "@/components/admin/ui";

export default function AdminUserDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { data: user, setData } = useApi(`/admin/users/${id}`);
  const { data: festivals } = useApi("/admin/festivals");
  const [form, setForm] = useState(null);
  const [password, setPassword] = useState("");
  const [owned, setOwned] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (user && !form) {
      setForm({ name: user.name || "", organizer_for: user.organizer_for || "", role: user.role, active: user.active });
      setOwned(user.festivals.map((f) => f.id));
    }
  }, [user, form]);

  if (!form || !user) return <p className="text-[14px] text-[#6B7280]">Loading…</p>;

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put(`/admin/users/${id}`, { ...form, password: password || undefined });
      await api.put(`/admin/users/${id}/festivals`, { festival_ids: owned });
      const { data: fresh } = await api.get(`/admin/users/${id}`);
      setData(fresh);
      setPassword("");
      toast.success(`Saved ${data.email}`);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!window.confirm(`Delete ${user.email}? Their festivals stay on the site but lose their owner.`)) return;
    try {
      await api.delete(`/admin/users/${id}`);
      toast.success("Account deleted");
      navigate("/admin/users");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <div data-testid="admin-user-detail-page">
      <Link to="/admin/users" className={`text-[13px] ${linkCls}`} data-testid="back-to-users">← All users</Link>
      <div className="mt-3">
        <PageTitle title={user.email}>
          <button type="button" className={btnPrimary} onClick={save} disabled={busy} data-testid="save-user-detail-button">{busy ? "Saving…" : "Save changes"}</button>
        </PageTitle>
      </div>

      <div className="space-y-5">
        <div className={`${card} space-y-4 p-5`} data-testid="user-profile-card">
          <div className="flex items-center justify-between">
            <p className="text-[15px] font-semibold">Profile</p>
            <Badge tone={user.stripe_connected ? "green" : "gray"}>{user.stripe_connected ? "payout account linked" : "no payout account"}</Badge>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Name"><input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} data-testid="user-name-input" /></Field>
            <Field label="Event organizer for"><input value={form.organizer_for} onChange={(e) => setForm({ ...form, organizer_for: e.target.value })} className={inputCls} data-testid="user-organizer-for-input" /></Field>
            <Field label="Role">
              <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className={inputCls} data-testid="user-role-select">
                <option value="organizer">Organizer</option>
                <option value="admin">Admin</option>
              </select>
            </Field>
            <Field label="Status">
              <select value={form.active ? "active" : "disabled"} onChange={(e) => setForm({ ...form, active: e.target.value === "active" })} className={inputCls} data-testid="user-status-select">
                <option value="active">Active</option>
                <option value="disabled">Disabled</option>
              </select>
            </Field>
          </div>
          <Field label="Reset password" hint="Leave blank to keep the current password.">
            <input value={password} onChange={(e) => setPassword(e.target.value)} placeholder="New password (8+ characters)" className={inputCls} data-testid="user-password-input" />
          </Field>
        </div>

        <div className={`${card} p-5`} data-testid="user-festivals-card">
          <p className="text-[15px] font-semibold">Festivals</p>
          <p className="mb-3 text-[12px] text-[#6B7280]">Tick the festivals this organizer manages. Their dashboard, sales and payouts are scoped to these.</p>
          <div className="max-h-[300px] divide-y divide-[#E5E7EB] overflow-auto rounded-md border border-[#E5E7EB]">
            {festivals?.map((f) => (
              <label key={f.id} className="flex cursor-pointer items-center gap-3 px-3 py-2 text-[13px] hover:bg-[#F9FAFB]">
                <input
                  type="checkbox"
                  checked={owned?.includes(f.id) || false}
                  onChange={() => setOwned(owned.includes(f.id) ? owned.filter((x) => x !== f.id) : [...owned, f.id])}
                  className="accent-[#B644DC]"
                  data-testid={`user-festival-${f.id}`}
                />
                <span className="flex-1 truncate">{f.name}</span>
                <span className="text-[11px] text-[#9CA3AF]">{f.video_count} videos</span>
              </label>
            ))}
            {festivals?.length === 0 && <p className="px-3 py-4 text-[12px] text-[#6B7280]">No festivals on the site yet.</p>}
          </div>
        </div>

        <div className={`${card} flex flex-col sm:flex-row sm:items-center justify-between gap-4 p-5`}>
          <div>
            <p className="text-[15px] font-semibold">Danger zone</p>
            <p className="text-[12px] text-[#6B7280]">Deleting an account keeps their festivals and videos on the site.</p>
          </div>
          <button type="button" className={btnDanger} onClick={remove} data-testid="delete-user-button">Delete account</button>
        </div>
      </div>
    </div>
  );
}
